"use client";

import type { ButtonHTMLAttributes, ReactNode } from "react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { getCtaDataAttrs } from "@/lib/analytics/cta-attrs";

type Props = Omit<ButtonProps, "children"> & {
	ctaId: string;
	ctaName: string;
	ctaLocation: string;
	children: ReactNode;
};

/**
 * Button that stamps `data-cta-*` attrs. Firing is handled by the global
 * `CtaClickTracker` (one document listener) — no per-click JS duplication.
 */
export default function TrackedCtaButton({
	ctaId,
	ctaName,
	ctaLocation,
	children,
	...rest
}: Props) {
	return (
		<Button
			{...rest}
			{...getCtaDataAttrs({ ctaId, ctaName, ctaLocation })}
		>
			{children}
		</Button>
	);
}

/** For native `<button>` without the design-system Button styles. */
export function TrackedNativeButton({
	ctaId,
	ctaName,
	ctaLocation,
	children,
	...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
	ctaId: string;
	ctaName: string;
	ctaLocation: string;
	children: ReactNode;
}) {
	return (
		<button
			type="button"
			{...rest}
			{...getCtaDataAttrs({ ctaId, ctaName, ctaLocation })}
		>
			{children}
		</button>
	);
}
